
import React, { useState } from 'react';
import { Wine } from '../types';
import { getSommelierAdvice } from '../services/geminiService';

const SommelierTool: React.FC<{ wines: Wine[] }> = ({ wines }) => {
  const [meal, setMeal] = useState('');
  const [advice, setAdvice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleAsk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!meal.trim()) return;
    
    setLoading(true);
    setAdvice(null);
    setCopied(false);
    try {
      const result = await getSommelierAdvice(wines, meal);
      setAdvice(result);
    } catch (error) {
      console.error(error);
      setAdvice("Servizio momentaneamente sospeso. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!advice) return;
    // Rimuoviamo i markdown dei grassetti per la copia pulita
    const cleanText = advice.replace(/\*\*/g, '');
    navigator.clipboard.writeText(cleanText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Funzione semplice per rendere i grassetti dell'AI
  const renderFormattedText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="text-amber-500 font-black">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="bg-white/5 p-8 rounded-[3rem] border border-white/5 text-center space-y-4 shadow-2xl">
        <div className="w-16 h-16 bg-amber-600/10 rounded-full flex items-center justify-center text-amber-500 mx-auto mb-4">
           <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
        </div>
        <h2 className="text-2xl font-bold text-amber-500 serif">AI Sommelier</h2>
        <p className="text-slate-400 text-xs max-w-[250px] mx-auto leading-relaxed">
          Descrivi il piatto e ti indicherò le migliori bottiglie della tua cantina.
        </p>

        <form onSubmit={handleAsk} className="mt-8 space-y-4">
          <textarea
            value={meal}
            onChange={(e) => setMeal(e.target.value)}
            placeholder="Esempio: Fiorentina al sangue con patate..."
            className="w-full bg-black/40 border border-white/10 rounded-[2rem] p-6 text-amber-100 placeholder:text-slate-700 focus:outline-none focus:border-amber-600/50 transition-colors resize-none h-28 text-sm"
          />
          <button
            type="submit"
            disabled={loading || !meal || wines.length === 0}
            className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-20 text-white font-black py-5 rounded-[2rem] transition-all shadow-xl uppercase tracking-widest text-xs active:scale-95"
          >
            {loading ? 'Consultando...' : 'Trova Abbinamento'}
          </button>
        </form>
      </div>

      {advice && (
        <div className="bg-white/[0.02] p-8 rounded-[3rem] border border-white/10 shadow-2xl animate-scale-in relative">
          <div className="flex items-center justify-between mb-8 border-b border-white/5 pb-4">
             <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500">
                   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <span className="text-[10px] font-black text-amber-500 uppercase tracking-[0.2em]">Consigli del Sommelier</span>
             </div>
             
             <button 
               onClick={copyToClipboard}
               className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${copied ? 'bg-green-500/20 text-green-500' : 'bg-white/5 text-slate-500 active:scale-90'}`}
             >
                {copied ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                )}
                <span className="text-[9px] font-black uppercase tracking-widest">{copied ? 'Copiato' : 'Copia'}</span>
             </button>
          </div>

          <div className="text-slate-300 leading-relaxed font-serif text-lg whitespace-pre-wrap">
            {renderFormattedText(advice)}
          </div>
        </div>
      )}

      {wines.length === 0 && (
         <div className="text-center p-8 border border-dashed border-white/5 rounded-[2rem]">
            <p className="text-[10px] text-slate-600 font-black uppercase tracking-widest">
              Aggiungi almeno una bottiglia per sbloccare i consigli.
            </p>
         </div>
      )}
    </div>
  );
};

export default SommelierTool;
